/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessApplyDirectoryRoleSetup } from "../funcs/accessApplyDirectoryRoleSetup.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ApplyDirectoryRoleSetupResult } from "../models/components/applydirectoryrolesetupresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ApplyDirectoryRoleSetupRequest,
  ApplyDirectoryRoleSetupSecurity,
} from "../models/operations/applydirectoryrolesetup.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ApplyDirectoryRoleSetupMutationVariables = {
  request: ApplyDirectoryRoleSetupRequest;
  security?: ApplyDirectoryRoleSetupSecurity | undefined;
  options?: RequestOptions;
};

export type ApplyDirectoryRoleSetupMutationData = ApplyDirectoryRoleSetupResult;

export type ApplyDirectoryRoleSetupMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * applyDirectoryRoleSetup access
 *
 * @remarks
 * Apply a reviewed bulk setup in one transaction: create the requested new roles and replace each listed source's role set. New roles get no grants unless grant_set is given, in which case every new role in the batch gets exactly that set. If any item's version no longer matches, nothing is written and the request returns conflict; preview again. WorkOS role sync for new roles is requested after commit and reported as requested, not done. Creating roles sets up no plugin distribution.
 */
export function useApplyDirectoryRoleSetupMutation(
  options?: MutationHookOptions<
    ApplyDirectoryRoleSetupMutationData,
    ApplyDirectoryRoleSetupMutationError,
    ApplyDirectoryRoleSetupMutationVariables
  >,
): UseMutationResult<
  ApplyDirectoryRoleSetupMutationData,
  ApplyDirectoryRoleSetupMutationError,
  ApplyDirectoryRoleSetupMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildApplyDirectoryRoleSetupMutation(client, options),
    ...options,
  });
}

export function mutationKeyApplyDirectoryRoleSetup(): MutationKey {
  return ["@gram/client", "access", "applyDirectoryRoleSetup"];
}

export function buildApplyDirectoryRoleSetupMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ApplyDirectoryRoleSetupMutationVariables,
  ) => Promise<ApplyDirectoryRoleSetupMutationData>;
} {
  return {
    mutationKey: mutationKeyApplyDirectoryRoleSetup(),
    mutationFn: function applyDirectoryRoleSetupMutationFn({
      request,
      security,
      options,
    }): Promise<ApplyDirectoryRoleSetupMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessApplyDirectoryRoleSetup(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
